import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { useAuth } from '../context/AuthContext';
import AttributeCheckbox from '../components/AttributeCheckbox';
import {
  Beer,
  MapPin,
  Building,
  FileText,
  Crosshair,
  CreditCard,
  Music,
  Sun,
  Clock,
  Check,
  Send,
  Camera,
  Popcorn
} from 'lucide-react';
import { trackEvent } from '../lib/posthog';
import { useLocationScope } from '../context/LocationContext';
import { getAreasForGroup, getMetroById } from '../data/metros';
import { getBroaderMetroLabel } from '../utils/barLocation';

function AddBar() {
  const navigate = useNavigate();
  const { submitBarRequest } = useApp();
  const { user, profile } = useAuth();
  const { enabledMetros, activeMetro } = useLocationScope();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [approvedBarId, setApprovedBarId] = useState(null);
  const [approvedBarName, setApprovedBarName] = useState('');
  const [error, setError] = useState('');

  const [form, setForm] = useState({
    name: '',
    address: '',
    metroId: activeMetro?.id || 'chicago',
    borough: '',
    neighborhood: '',
    description: '',
    attributes: {
      poolTable: false,
      darts: false,
      cashOnly: false,
      jukebox: false,
      outdoorSeating: false,
      liveMusic: false,
      happyHour: false,
      touchTunes: false,
      triviaNights: false,
      euchre: false,
      oldStyleSign: false,
      photoBooth: false,
      popcorn: false
    }
  });

  // Duplicate checking removed - community will flag duplicates later
  // This allows for faster submissions and edge cases where bars have similar names

  const selectedMetro = getMetroById(form.metroId);
  const usesBoroughs = Boolean(selectedMetro?.groups?.length);
  const broaderLabel = getBroaderMetroLabel(selectedMetro);
  const isBroaderBorough = usesBoroughs && form.borough === broaderLabel;
  const neighborhoodOptions = usesBoroughs
    ? getAreasForGroup(selectedMetro, form.borough)
    : (selectedMetro?.areas || []);
  const neighborhoodDisabled = usesBoroughs && (!form.borough || isBroaderBorough);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
    setError('');
  };

  const handleMetroChange = (e) => {
    const metroId = e.target.value;
    setForm((prev) => ({
      ...prev,
      metroId,
      borough: '',
      neighborhood: '',
      attributes: {
        ...prev.attributes,
        euchre: metroId === 'chicago' || metroId === 'milwaukee' ? prev.attributes.euchre : false,
        oldStyleSign: metroId === 'chicago' ? prev.attributes.oldStyleSign : false,
      },
    }));
    setError('');
  };

  const handleBoroughChange = (e) => {
    const borough = e.target.value;
    setForm((prev) => ({
      ...prev,
      borough,
      neighborhood: '',
    }));
    setError('');
  };

  const handleAttributeChange = (key, value) => {
    setForm(prev => ({
      ...prev,
      attributes: { ...prev.attributes, [key]: value }
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!form.name || !form.address || !form.metroId) {
      setError('Please fill in the bar name, city, and address');
      return;
    }
    if (usesBoroughs && !form.borough) {
      setError('Please pick a borough');
      return;
    }
    if (!isBroaderBorough && !form.neighborhood) {
      setError('Please pick a neighborhood');
      return;
    }

    if (!user || !profile) {
      setError('You must be logged in to submit a bar');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      const result = await submitBarRequest({
        ...form,
        neighborhood: isBroaderBorough ? broaderLabel : form.neighborhood,
      });
      setSuccess(true);
      trackEvent('bar_added', { bar_name: form.name, neighborhood: isBroaderBorough ? broaderLabel : form.neighborhood });
      if (result?.id) {
        setApprovedBarId(result.id);
      }
      if (result?.name) {
        setApprovedBarName(result.name);
      }
    } catch (error) {
      console.error('Error submitting bar:', error);
      setError(error.message || 'Failed to submit bar. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-dive-black py-6 px-4">
        <div className="max-w-2xl mx-auto">
          <div className="text-center py-16 bg-dive-dark rounded-xl border border-dive-gray">
            <Send className="w-16 h-16 text-dive-neon mx-auto mb-4" />
            <h1 className="text-2xl font-display text-white mb-3">Know a Great Dive Bar?</h1>
            <p className="text-gray-400 mb-6 max-w-md mx-auto">
              Sign in to submit a bar to our community! Help fellow dive bar enthusiasts discover hidden gems.
            </p>
            <Link to="/auth" className="btn-primary inline-flex items-center gap-2">
              <img src="/logo-icon.png" alt="" className="w-5 h-5 object-contain" />
              Sign In or Create Account
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-dive-black flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <div className="w-20 h-20 bg-dive-green/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <Check className="w-10 h-10 text-dive-green" />
          </div>
          <h2 className="text-white text-2xl font-display mb-2">✓ Confirmed: Bar Added!</h2>
          {approvedBarName && (
            <p className="text-dive-neon text-lg font-medium mb-3">
              "{approvedBarName}"
            </p>
          )}
          <p className="text-gray-400 mb-6">
            Your bar is now live on Tilted and ready for the community to rate!
          </p>
          <div className="flex flex-col gap-3 items-center">
            {approvedBarId && (
              <Link to={`/bar/${approvedBarId}`} className="btn-primary w-full max-w-xs">
                View {approvedBarName || 'Bar'} →
              </Link>
            )}
            <div className="flex gap-3">
              <Link to="/list" className="btn-secondary">
                Browse Bars
              </Link>
              <button
                onClick={() => {
                  setSuccess(false);
                  setApprovedBarId(null);
                  setApprovedBarName('');
                  setForm({
                    name: '',
                    address: '',
                    metroId: activeMetro?.id || 'chicago',
                    borough: '',
                    neighborhood: '',
                    description: '',
                    attributes: {
                      poolTable: false,
                      darts: false,
                      cashOnly: false,
                      jukebox: false,
                      outdoorSeating: false,
                      liveMusic: false,
                      happyHour: false,
                      touchTunes: false,
                      triviaNights: false,
                      euchre: false,
                      oldStyleSign: false,
                      photoBooth: false,
                      popcorn: false
                    }
                  });
                }}
                className="btn-secondary"
              >
                Add Another
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-dive-black py-6">
      <div className="max-w-2xl mx-auto px-4">
        {/* Header */}
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 bg-dive-neon/20 rounded-xl flex items-center justify-center">
            <Send className="w-6 h-6 text-dive-neon" />
          </div>
          <div>
            <h1 className="font-display text-2xl text-white tracking-wide">
              ADD A BAR
            </h1>
            <p className="text-gray-400 text-sm">
              Know a dive we're missing? Add it to the map!
            </p>
          </div>
        </div>

        <div className="bg-amber-500/10 border border-amber-500/50 rounded-lg p-4 mb-6">
          <div className="flex items-start gap-3">
            <MapPin className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-amber-400 text-sm font-medium mb-1">Enabled cities only</p>
              <p className="text-gray-400 text-sm">
                Bars must fall within 50 miles of an enabled Tilted city.
              </p>
            </div>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-500/20 border border-red-500 text-red-400 px-4 py-3 rounded-lg mb-6 text-sm">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Bar Name */}
          <div>
            <label className="text-white font-medium mb-2 flex items-center gap-2">
              <img src="/logo-icon.png" alt="" className="w-4 h-4 object-contain" />
              Bar Name *
            </label>
            <input
              type="text"
              name="name"
              value={form.name}
              onChange={handleChange}
              placeholder="e.g., The Empty Glass"
              className="w-full"
              required
            />
          </div>

          {/* Address */}
          <div>
            <label className="text-white font-medium mb-2 flex items-center gap-2">
              <MapPin className="w-4 h-4 text-dive-neon" />
              Address *
            </label>
            <input
              type="text"
              name="address"
              value={form.address}
              onChange={handleChange}
              placeholder="e.g., 1234 N Milwaukee Ave"
              className="w-full"
              required
            />
          </div>

          <div>
            <label className="text-white font-medium mb-2 flex items-center gap-2">
              <Building className="w-4 h-4 text-dive-neon" />
              City *
            </label>
            <select
              name="metroId"
              value={form.metroId}
              onChange={handleMetroChange}
              className="w-full"
              required
            >
              {enabledMetros.map((metro) => (
                <option key={metro.id} value={metro.id}>
                  {metro.name}, {metro.state}
                </option>
              ))}
            </select>
            <p className="text-gray-500 text-xs mt-1">
              The region you drink in — suburbs count. This is not city-limit only.
            </p>
          </div>

          {usesBoroughs && (
            <div>
              <label className="text-white font-medium mb-2 flex items-center gap-2">
                <Building className="w-4 h-4 text-dive-neon" />
                {selectedMetro?.groupLabel || 'Borough'} *
              </label>
              <select
                name="borough"
                value={form.borough}
                onChange={handleBoroughChange}
                className="w-full"
                required
              >
                <option value="">Select a borough</option>
                <option value={broaderLabel}>{broaderLabel}</option>
                {selectedMetro.groups.map((group) => (
                  <option key={group.name} value={group.name}>
                    {group.name}
                  </option>
                ))}
              </select>
              <p className="text-gray-500 text-xs mt-1">
                Jersey, Long Island, and other suburbs: pick {broaderLabel}.
              </p>
            </div>
          )}

          {/* Neighborhood */}
          <div>
            <label className={`text-white font-medium mb-2 flex items-center gap-2 ${neighborhoodDisabled ? 'opacity-50' : ''}`}>
              <Building className="w-4 h-4 text-dive-neon" />
              {(selectedMetro?.areaLabel) || 'Neighborhood'} {isBroaderBorough ? '' : '*'}
            </label>
            <select
              name="neighborhood"
              value={form.neighborhood}
              onChange={handleChange}
              className={`w-full ${neighborhoodDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
              required={!neighborhoodDisabled}
              disabled={neighborhoodDisabled}
            >
              <option value="">
                {isBroaderBorough ? 'Not needed for broader metro' : 'Select an area'}
              </option>
              {!usesBoroughs && (
                <option value={broaderLabel}>{broaderLabel}</option>
              )}
              {neighborhoodOptions.map(n => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
            <p className="text-gray-500 text-xs mt-1">
              {usesBoroughs
                ? (isBroaderBorough
                  ? 'The map will show the actual town from the address.'
                  : 'Neighborhoods are filtered by the borough you picked.')
                : `Suburbs and nearby towns: pick ${broaderLabel}. The map will show the actual town from the address.`}
            </p>
          </div>

          {/* Description */}
          <div>
            <label className="text-white font-medium mb-2 flex items-center gap-2">
              <FileText className="w-4 h-4 text-dive-neon" />
              Why is this a dive bar?
            </label>
            <textarea
              name="description"
              value={form.description}
              onChange={handleChange}
              placeholder="Tell us what makes this place a dive bar worth visiting..."
              rows={3}
              className="w-full resize-none"
            />
          </div>

          {/* Attributes */}
          <div>
            <label className="text-white font-medium mb-3 block">
              Features (if you know them)
            </label>
            <div className="grid grid-cols-2 gap-2">
              <AttributeCheckbox
                label="Pool Table"
                checked={form.attributes.poolTable}
                onChange={(val) => handleAttributeChange('poolTable', val)}
                icon={Crosshair}
              />
              <AttributeCheckbox
                label="Darts"
                checked={form.attributes.darts}
                onChange={(val) => handleAttributeChange('darts', val)}
                icon={Crosshair}
              />
              <AttributeCheckbox
                label="Cash Only"
                checked={form.attributes.cashOnly}
                onChange={(val) => handleAttributeChange('cashOnly', val)}
                icon={CreditCard}
              />
              <AttributeCheckbox
                label="Jukebox"
                checked={form.attributes.jukebox}
                onChange={(val) => handleAttributeChange('jukebox', val)}
                icon={Music}
              />
              <AttributeCheckbox
                label="Patio"
                checked={form.attributes.outdoorSeating}
                onChange={(val) => handleAttributeChange('outdoorSeating', val)}
                icon={Sun}
              />
              <AttributeCheckbox
                label="Live Music"
                checked={form.attributes.liveMusic}
                onChange={(val) => handleAttributeChange('liveMusic', val)}
                icon={Music}
              />
              <AttributeCheckbox
                label="Happy Hour"
                checked={form.attributes.happyHour}
                onChange={(val) => handleAttributeChange('happyHour', val)}
                icon={Clock}
              />
              <AttributeCheckbox
                label="TouchTunes"
                checked={form.attributes.touchTunes}
                onChange={(val) => handleAttributeChange('touchTunes', val)}
                icon={Music}
              />
              <AttributeCheckbox
                label="Trivia Nights"
                checked={form.attributes.triviaNights}
                onChange={(val) => handleAttributeChange('triviaNights', val)}
                icon={Crosshair}
              />
              {(form.metroId === 'chicago' || form.metroId === 'milwaukee') && (
                <AttributeCheckbox
                  label="Euchre"
                  checked={form.attributes.euchre}
                  onChange={(val) => handleAttributeChange('euchre', val)}
                  icon={Crosshair}
                />
              )}
              {form.metroId === 'chicago' && (
                <AttributeCheckbox
                  label="Old Style Sign"
                  checked={form.attributes.oldStyleSign}
                  onChange={(val) => handleAttributeChange('oldStyleSign', val)}
                  icon={Beer}
                />
              )}
              <AttributeCheckbox
                label="Photo Booth"
                checked={form.attributes.photoBooth}
                onChange={(val) => handleAttributeChange('photoBooth', val)}
                icon={Camera}
              />
              <AttributeCheckbox
                label="Popcorn"
                checked={form.attributes.popcorn}
                onChange={(val) => handleAttributeChange('popcorn', val)}
                icon={Popcorn}
              />
            </div>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isSubmitting}
            className="btn-primary w-full flex items-center justify-center gap-2"
          >
            {isSubmitting ? (
              <>
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Adding Bar...
              </>
            ) : (
              <>
                <Send className="w-5 h-5" />
                Add Bar
              </>
            )}
          </button>
        </form>
      </div>
    </div>
  );
}

export default AddBar;
